"use client";
import { useEffect, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { usePageVisibility } from "@/hooks/use-page-visibility";

const wordSegmenter = new Intl.Segmenter(undefined, { granularity: "word" });
const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

export const FlipWords = ({
  words,
  duration = 3000,
  loop = true,
  className,
}: {
  words: string[];
  duration?: number;
  loop?: boolean;
  className?: string;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasRotated, setHasRotated] = useState(false);
  const [shouldReduceMotion, setShouldReduceMotion] = useState(false);
  const pageVisible = usePageVisibility();
  const currentWord = words[currentIndex] ?? words[0] ?? "";

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setShouldReduceMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!pageVisible || shouldReduceMotion || words.length < 2 || (!loop && currentIndex === words.length - 1)) return;

    const timeout = window.setTimeout(() => {
      setHasRotated(true);
      setCurrentIndex((index) => (index + 1 < words.length ? index + 1 : 0));
    }, duration);
    return () => window.clearTimeout(timeout);
  }, [currentIndex, duration, loop, pageVisible, shouldReduceMotion, words.length]);

  if (shouldReduceMotion) {
    return (
      <span className={cn("inline-block text-left text-neutral-900 dark:text-neutral-100", className)}>
        {words[0]}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "relative z-10 inline-block px-2 text-left text-neutral-900 dark:text-neutral-100",
        className,
      )}
      key={currentWord}
    >
      {Array.from(wordSegmenter.segment(currentWord), ({ segment }) => segment).map((word, wordIndex) => (
        <span key={word + wordIndex} className="inline-block whitespace-pre">
          {Array.from(graphemeSegmenter.segment(word), ({ segment }) => segment).map((letter, letterIndex) => (
            <span
              key={word + letterIndex}
              className={hasRotated ? "flip-word-letter inline-block" : "inline-block"}
              style={
                {
                  "--flip-delay": `${wordIndex * 150 + letterIndex * 25}ms`,
                } as CSSProperties
              }
            >
              {letter}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
};
